import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavigationContext } from '../../providers/NavigationProvider';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuthentication';
import { McoLsvOrderAWS, WarrantyRegistrationAWS } from '../../@types/AWS';
import { ZendeskTicket, ZendeskTicketComment, ZendeskUser } from '../../@types/Zendesk';
import { toast } from 'react-toastify';
import axiosDealerPortalApi from '../../api/axios';
import { RoleType } from '../../@types/Auth';
import { Divider, List, ListItem, ListItemText, Skeleton, Stack, Typography } from '@mui/material';
import Card from '../common/Card/Card';
import { SubHeading } from '../common/PageHeadings/PageHeadings';
import moment from 'moment';
import PrintIcon from '@mui/icons-material/Print';
import LoadingIndicator from '../common/LoadingIndicator/LoadingIndicator';
import CardAccordion from '../common/CardAccordion/CardAccordion';
import ZendeskStatusDisplay from '../ZendeskStatusDisplay/ZendeskStatusDisplay';
import { useReactToPrint } from 'react-to-print';
import ZendeskMadjaxBadge from '../ZendeskBadge/ZendeskBadge';
import Button from '../common/Button';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import axios from '../../api/axios';
import ZendeskBadge from '../ZendeskBadge/ZendeskBadge';


interface VehicleHistoryDetailPageProps { }

const VehicleHistoryDetailPage: FC<VehicleHistoryDetailPageProps> = () => {

  const { user } = useAuth();

  const params = useParams();

  const navigate = useNavigate();

  const navContextData = useContext(NavigationContext);

  const [vehicleWarrantyRegistration, setVehicleWarrantyRegistration] = useState<WarrantyRegistrationAWS | undefined>(undefined);

  const [vehicleMcoLsvOrder, setVehicleMcoLsvOrder] = useState<McoLsvOrderAWS | undefined>(undefined);

  const [loadingWarrantyRegistration, setLoadingWarrantyRegistration] = useState<boolean>(false);

  const [loadingVehicleMcoLsv, setLoadingVehicleMcoLsv] = useState<boolean>(false);

  const [vehicleWarrantyClaimTickets, setVehicleWarrantyClaimTickets] = useState<ZendeskTicket[]>([]);

  const [loadingWarrantyClaims, setLoadingWarrantyClaims] = useState<boolean>(false);

  const loadVehicleWarrantyRegistration = useCallback(async () => {

    try {

      setLoadingWarrantyRegistration(true);

      const queryParams = { vehicleSerialNumber: params.id };

      const response = await axiosDealerPortalApi.get('/warranty/cart/registration-search', { params: queryParams });

      const foundWarrantyRegistrations = response?.data?.data;

      const warrantyRegistration = foundWarrantyRegistrations[0] as WarrantyRegistrationAWS;

      if (warrantyRegistration && warrantyRegistration?.dealerInformation?.dealerId !== user?.companyAttributes?.dealerId && user?.attributes?.type !== RoleType.Admin) {

        toast.error(`Serial Number ${params.id} is not associated with Dealer Account #${user?.companyAttributes?.dealerId}`);

        navigate('/vehicle-history');

      }

      setVehicleWarrantyRegistration(warrantyRegistration);

      navContextData?.setRouteDisplayText(`Vehicle History Details ${warrantyRegistration?.vehicleInformation?.vehicleSerialNumber ?? params.id}`)

    } catch (error: any) {

      console.log(error);

      toast.error('Error loading vehicle warranty registration data');

    } finally {

      setLoadingWarrantyRegistration(false);

    }

  }, [

    user,

    params,

    setVehicleWarrantyRegistration,

    setLoadingWarrantyRegistration

  ]);

  const loadVehicleMcoLsvOrder = useCallback(async () => {

    try {

      setLoadingWarrantyRegistration(true);

      const queryParams = { vehicleSerialNumber: params.id };

      const response = await axiosDealerPortalApi.get('/orders/mco-lsv-search', { params: queryParams });

      const foundMcoLsvOrders = response?.data?.data as McoLsvOrderAWS[];

      const foundMcoLsvOrder = foundMcoLsvOrders[0] as McoLsvOrderAWS;

      setVehicleMcoLsvOrder(foundMcoLsvOrder);

    } catch (error: any) {

      console.log(error);

      toast.error('Error loading vehicle mco / lsv data');

    } finally {

      setLoadingWarrantyRegistration(false);

    }


  }, [

    user,

    params,

    setVehicleMcoLsvOrder,

    setLoadingVehicleMcoLsv

  ]);

  const loadVehicleZendeskWarrantyClaimTickets = useCallback(async () => {

    try {

      setLoadingWarrantyClaims(true);

      let queryString = `${params.id} `;

      /* 
      if (!isMadJaxAdmin(user))
        queryString += `custom_field_14985663314199:${user?.companyAttributes?.dealerId} `

      */

      queryString += `tags:warranty-claim`;

      const queryParams = { query: queryString, 'filter[type]': 'ticket', 'page[size]': 100, order_by: 'created_at', sort: 'desc' };

      const response = await axios.get(`/zendesk/search`, { params: queryParams });

      const tickets = response.data.results as ZendeskTicket[];

      setVehicleWarrantyClaimTickets(tickets);

    } catch (error) {

      console.log(error);

      toast.error(`Error loading vehicle ${params.id} warranty claim tickets`);

    } finally {

      setLoadingWarrantyClaims(false);

    }

  }, [

    user,

    setVehicleWarrantyClaimTickets,

    setLoadingWarrantyClaims

  ]);

  useEffect(() => {

    loadVehicleMcoLsvOrder();

    loadVehicleWarrantyRegistration();

    loadVehicleZendeskWarrantyClaimTickets();

  }, [loadVehicleWarrantyRegistration, loadVehicleZendeskWarrantyClaimTickets, loadVehicleMcoLsvOrder]);

  return (

    <Stack direction="column" spacing={4}>

      <Stack direction="column" spacing={2}>

        <SubHeading>Vehicle Registration</SubHeading>

        <Card>

          <WarrantyRegistrationDetail loading={loadingWarrantyRegistration} vehicleWarrantyRegistration={vehicleWarrantyRegistration} />

        </Card>

      </Stack>

      <Stack direction="column" spacing={2}>

        <SubHeading>Vehicle MCO | LSV</SubHeading>

        <Card>

          <McoLsvOrderDetail loading={loadingVehicleMcoLsv} vehicleMcoLsvOrder={vehicleMcoLsvOrder} vehicleSerial={params.id} />

        </Card>

      </Stack>

      <Stack direction="column" spacing={2}>

        <SubHeading>Vehicle Warranty Claims</SubHeading>

        <WarrantyClaimTicketList loading={loadingWarrantyClaims} tickets={vehicleWarrantyClaimTickets} />

      </Stack>

    </Stack>

  );

}

interface WarrantyRegistrationDetailProps {

  loading: boolean

  vehicleWarrantyRegistration: WarrantyRegistrationAWS | undefined;

}

const WarrantyRegistrationDetail: FC<WarrantyRegistrationDetailProps> = ({ loading, vehicleWarrantyRegistration }) => {

  const { user } = useAuth();

  const userIsRegisteredDealer = user?.companyAttributes?.dealerId === vehicleWarrantyRegistration?.dealerInformation?.dealerId;

  return (

    <Stack direction="column" spacing={2}>

      {

        vehicleWarrantyRegistration

          ? (

            <List

              sx={{

                paddingLeft: '25px',

                paddingRight: '25px',

                '.MuiListItem-root': {

                  paddingLeft: 0,

                  paddingRight: 0,

                  '.MuiListItemText-root:last-of-type': {

                    textAlign: 'right',

                    maxWidth: '50%'

                  }

                }

              }}>

              {(userIsRegisteredDealer || isMadJaxAdmin(user)) &&

                <ListItem>

                  <ListItemText>Dealer Name:</ListItemText>

                  <ListItemText>

                    {loading

                      ? <Skeleton animation="wave" height={20} />

                      : vehicleWarrantyRegistration?.dealerInformation?.dealerName ?? 'N/A'

                    }

                  </ListItemText>

                </ListItem>

              }

              {(userIsRegisteredDealer || isMadJaxAdmin(user)) &&

                <ListItem>

                  <ListItemText>Dealer ID:</ListItemText>

                  <ListItemText>

                    {loading

                      ? <Skeleton animation="wave" height={20} />

                      : vehicleWarrantyRegistration?.dealerInformation?.dealerId ?? 'N/A'

                    }

                  </ListItemText>

                </ListItem>

              }

              <ListItem>

                <ListItemText>Serial Number:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.vehicleInformation?.vehicleSerialNumber

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>Date Created:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : moment(new Date(vehicleWarrantyRegistration?.dateCreated ?? '')).format('MM/DD/YYYY')

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>Date Purchased:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : moment(new Date(vehicleWarrantyRegistration?.vehicleInformation?.datePurchased ?? '')).format('MM/DD/YYYY')

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>LSV Package:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.vehicleInformation?.vehicleHasLSVPackage ? 'LSV Package' : 'No LSV Package'

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>VIN:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.vehicleInformation?.vehicleVin ?? 'N/A'

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>Warranty Type:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.vehicleInformation?.vehicleWarrantyType

                  }

                </ListItemText>

              </ListItem>

              {(vehicleWarrantyRegistration?.ownerInformation && 'company' in vehicleWarrantyRegistration?.ownerInformation) &&

                <ListItem>

                  <ListItemText>Company:</ListItemText>

                  <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.company}</ListItemText>

                </ListItem>

              }

              {(vehicleWarrantyRegistration?.ownerInformation && 'firstName' in vehicleWarrantyRegistration.ownerInformation) &&

                <ListItem>

                  <ListItemText>First Name:</ListItemText>

                  <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.firstName}</ListItemText>

                </ListItem>

              }

              {(vehicleWarrantyRegistration?.ownerInformation && 'lastName' in vehicleWarrantyRegistration.ownerInformation) &&

                <ListItem>

                  <ListItemText>Last Name:</ListItemText>

                  <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.lastName}</ListItemText>

                </ListItem>

              }

              <ListItem>

                <ListItemText>Email:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.ownerInformation?.ownerEmail

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>Phone:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.ownerInformation?.phone

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>Address:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleWarrantyRegistration?.ownerInformation?.address?.street

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText></ListItemText>

                <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.address?.street2}</ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText></ListItemText>

                <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.address?.city}</ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText></ListItemText>

                <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.address?.state}</ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText></ListItemText>

                <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.address?.zip}</ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText></ListItemText>

                <ListItemText>{vehicleWarrantyRegistration?.ownerInformation?.address?.country}</ListItemText>

              </ListItem>

            </List>

          )

          : (<Typography>Vehicle is unregistered</Typography>)

      }

    </Stack>

  )

}

interface McoLsvOrderDetailProps {

  loading: boolean

  vehicleMcoLsvOrder: McoLsvOrderAWS | undefined;

  vehicleSerial: string | undefined;

}

const McoLsvOrderDetail: FC<McoLsvOrderDetailProps> = ({ loading, vehicleMcoLsvOrder, vehicleSerial }) => {

  const { user } = useAuth();

  const foundVehicle = vehicleMcoLsvOrder?.vehicles.find((vehicle) => vehicle.vehicleSerialNumber.toLowerCase() == vehicleSerial?.toLowerCase());

  return (

    <Stack direction="column" spacing={2}>

      {

        foundVehicle

          ? (

            <List

              sx={{

                paddingLeft: '25px',

                paddingRight: '25px',

                '.MuiListItem-root': {

                  paddingLeft: 0,

                  paddingRight: 0,

                  '.MuiListItemText-root:last-of-type': {

                    textAlign: 'right',

                    maxWidth: '50%'

                  }

                }

              }}>

              <ListItem>

                <ListItemText>Ordered By:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : vehicleMcoLsvOrder?.dealerInformation?.dealerName ?? 'Unknown'

                  }

                </ListItemText>


              </ListItem>

              <ListItem>

                <ListItemText>Date Ordered:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : moment(new Date(vehicleMcoLsvOrder?.dateCreated ?? '')).format('MM/DD/YYYY')

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>MCO:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : foundVehicle.mco ? 'Yes' : 'No'

                  }

                </ListItemText>

              </ListItem>

              <ListItem>

                <ListItemText>LSV Package:</ListItemText>

                <ListItemText>

                  {loading

                    ? <Skeleton animation="wave" height={20} />

                    : foundVehicle?.lsvKit ? 'LSV Package' : 'No LSV Package'

                  }

                </ListItemText>

              </ListItem>


            </List>

          )

          : (<Typography>Vehicle has no MCO or LSV orders</Typography>)

      }

    </Stack>
  )
}

interface WarrantyClaimTicketListProps {

  loading: boolean

  tickets: ZendeskTicket[]

}

const WarrantyClaimTicketList: FC<WarrantyClaimTicketListProps> = ({ loading, tickets }) => {

  const navigate = useNavigate();

  return (loading

    ? (<LoadingIndicator loading={loading} />)

    : (

      !tickets.length

        ? (

          <Card>

            <Stack direction="column" spacing={2} justifyContent="center" alignItems="center" padding="24px">

              <Typography>No Tickets Found</Typography>

              <Button sx={{ width: 'fit-content ' }} onClick={() => navigate('/warranty-claims/new-ticket')}>Create New Warranty Claim</Button>

            </Stack>

          </Card>

        )

        : (

          tickets.map((ticket) => {

            const ticketFields = ticket.fields;

            const dealerReferenceIdField = ticketFields.find((field) => field.id === 40735979355031);

            return (

              <CardAccordion

                key={ticket.id}

                title={`Ticket #${ticket.id} (${moment(new Date(ticket.created_at)).format('MM/DD/YYYY')}) ${dealerReferenceIdField?.value ? `- ${dealerReferenceIdField.value}` : ''}`}

                titleAdditionalIcon={<ZendeskStatusDisplay ticketStatus={ticket.status} />}>

                <WarrantyClaimTicketDetail ticket={ticket} />

              </CardAccordion>

            )

          })

        )

    )

  )

}

interface WarrantyClaimTicketDetailProps {

  ticket: ZendeskTicket;

}

const WarrantyClaimTicketDetail: FC<WarrantyClaimTicketDetailProps> = ({ ticket }) => {

  const [users, setUsers] = useState<ZendeskUser[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const [comments, setComments] = useState<ZendeskTicketComment[]>([]);

  const loadZendeskTicketComments = useCallback(async () => {

    try {

      console.log(ticket);

      setLoading(true);

      // store list of users IDs to pull from Zendesk
      const userIds: number[] = [];

      // Add requester to users list
      userIds.push(ticket.requester_id);

      // Load comments associated with ticket
      const commentApiResponse = await axios.get(`/zendesk/ticket/comments`, { params: { ticketId: ticket.id } });

      const comments = commentApiResponse?.data?.data as ZendeskTicketComment[] ?? [];

      setComments(comments);

      // Add comment authors to user list
      comments.forEach((comment) => {

        if (userIds.some((user) => user !== comment.author_id)) userIds.push(comment.author_id);

      });

      // Load users associated with ticket
      const apiParams = { userIds: userIds.join(',') };

      const usersResponse = await axios.get(`/zendesk/users/show-many`, { params: apiParams });

      const users = usersResponse?.data?.data as ZendeskUser[];

      setUsers(users);

    } catch (error) {

      console.log(error);

      toast.error(`Error loading warranty claim comments ${ticket.id}`);

    } finally {

      setLoading(false);

    }

  }, [ticket]);

  useEffect(() => {

    loadZendeskTicketComments()

  }, [loadZendeskTicketComments, ticket])

  const getUser = (userId: number): ZendeskUser | undefined => users.find((user) => user.id === userId);

  const contentRef = useRef<HTMLDivElement>(null);

  const reactToPrintFn = useReactToPrint({ contentRef });

  const { user } = useAuth();

  return (

    loading

      ? (<LoadingIndicator loading={loading} />)

      : (

        <Stack direction="column" spacing={2} divider={<Divider />}>

          {comments.map((comment, index) => {

            const parser = new DOMParser();

            let html = comment.html_body;

            const doc = parser.parseFromString(html, 'text/html');

            doc.querySelectorAll('.signature').forEach(element => element.remove());

            html = doc.documentElement.innerHTML;

            const result = doc.querySelector('.zd-comment div');

            if (result) {

              html = result.innerHTML;

            }

            return (

              <Card key={index}>

                <Stack direction="column" spacing={2}>

                  <Stack direction="row" flexWrap="wrap" justifyContent="space-between" rowGap={2} columnGap={2}>

                    <ZendeskBadge zendeskUser={getUser(comment.author_id)} user={user} />

                    <Typography>{moment(new Date(comment.created_at as string)).format('MM/DD/YYYY h:mm A')}</Typography>

                  </Stack>

                  <Stack direction="column" spacing={2}>

                    {result &&

                      <Button sx={{ width: 'fit-content', alignSelf: 'flex-end' }} startIcon={<PrintIcon />} onClick={reactToPrintFn}>Print</Button>

                    }
                    <div ref={result ? contentRef : undefined} dangerouslySetInnerHTML={{ __html: html }} />

                  </Stack>

                </Stack>

              </Card>

            )

          })}

        </Stack>

      )

  )

}

export default VehicleHistoryDetailPage;
